import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import logger from "@insite/client-framework/Logger";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAccessibleSubmit from "@MasterBluePrint/Common/Hooks/useAccessibleSubmit";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@MasterBluePrint/Overrides/Components/SmallHeadingAndText";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: HasCartLineContext & OwnProps) => {
    const productState = getProductState(state, ownProps.cartLine.productId ?? undefined);
    return {
        isLoading: state.data.carts.isLoading,
        cartLoadedTime: state.data.carts.cartLoadedTime,
        fetchedProduct: productState?.value,
    };
};

const mapDispatchToProps = {
    loadProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    cartlineContainer?: InjectableCss;
    minMultiGridItem?: InjectableCss;
    minMultiWrapper?: InjectableCss;
    minQtyLabel?: TypographyPresentationProps;
    minQtyValue?: TypographyPresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: .625rem;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
    readOnlyQuantityText: {
        extendedStyles: {
            text: {
                css: css`
                    font-weight:bold;
                    font-size:.95rem !important;
                `,
            },
        },
    },
    cartlineContainer: {
        css: css`
            text-transform: uppercase;
            label {
                text-transform: uppercase;
            }
        `,
    },
    minMultiGridItem: {
        css: css`
            display:block;
            border-top:1px solid #CCC;
            padding-top:10px;
        `
    },
    minMultiWrapper: {
        css: css`            
            font-size: .9rem !important;
            word-break: normal;
            margin-top: 0;
            margin-bottom:.25rem;
            line-height:1.3;
            position: relative;
            display: block;
            color: #333F48;
            font-family: 'Lato', sans-serif;
            white-space:nowrap;
            font-style:italic;
        `,
    },
    minQtyLabel: {
        css: css`
            margin-right:2px;
            font-weight: 600;
        `,
    },
    minQtyValue: {
        css: css`
            font-weight:400;
        `,
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    fetchedProduct,
    loadProduct,
    onQtyOrderedChange = () => { },
    extendedStyles,
}) => {
    useEffect(() => {
    if (!fetchedProduct && cartLine?.productId) {
        loadProduct({ id: cartLine.productId });
    }
}, [cartLine?.productId, fetchedProduct]);

    const product = mapCartLineToProduct(cartLine, fetchedProduct);

    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }
        onQtyOrderedChange(inputQty);
    };

    const toasterContext = useContext(ToasterContext);
    const [productQty, setProductQty] = useState(cartLine?.qtyOrdered?.toString());
    const [multipleSaleQty, setMultipleSaleQty] = React.useState(Number(cartLine?.properties?.multipleSaleQty));

    const keyUpHandler = () => {
        if (multipleSaleQty && multipleSaleQty > 1) {
            document.querySelector('[data-test-selector="cartTotal_Checkout"]')?.removeAttribute("disabled");
            document.querySelector('[data-test-selector="cartTotal_SubmitQuote"]')?.removeAttribute("disabled");
            document
                .querySelector(`[data-test-selector="addtolist_${cartLine.id}"]`)
                ?.setAttribute("style", "display:block");
        }
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLine,
        toasterContext,
        cartLoadedTime,
    );

    useEffect(() => {
        if (cartLine.qtyOrdered === Number(productQty)) {
            return;
        }

        setProductQty(`${cartLine.qtyOrdered || ""}`);
        const possibleNumber = Number(cartLine?.qtyOrdered);
        onQtyOrderedChange!(possibleNumber);
    }, [cartLine.qtyOrdered]);

    const increaseOrderedProductQty = () => {
        const amount = Number(productQty) + 1;

        if ((cartLine.properties?.cartLineCondition?.toLowerCase() === "open box" || cartLine.properties?.cartLineCondition?.toLowerCase() === "used") && (amount > cartLine.qtyOnHand)) {
            toasterContext.addToast({ body: siteMessage("Core_NotEnoughStockError"), messageType: "danger" });
            return;
        }

        setProductQty(amount.toString());

        if (!onQtyOrderedChange) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChange(possibleNumber);
        }
    };

    const decreaseOrderedProductQty = () => {
        const amount = Number(productQty) > 1 ? Number(productQty) - 1 : Number(productQty);

        setProductQty(amount.toString());

        if (!onQtyOrderedChange) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChange(possibleNumber);
        }
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ".") {
            event.preventDefault();
        }
    };

    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    useEffect(() => {
        if (cart.properties?.isquote === "true") {
            setIsQuoteCart(true);
        }
    }, [cart]);

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <GridContainer gap={8} style={{ display: "block", width: "100%" }} className="qty-container">                
                <GridItem width={12}>
                    <StyledWrapper style={{ position: "relative" }} className="input-container">
                        <TextField
                            {...styles.editableQuantityTextField}
                            type="text"
                            min={0}
                            max={999999}
                            label={label || translate("Quantity:")}
                            value={FormattedCommaSeparatedValue(value)}
                            disabled={
                                !cart.canModifyOrder ||
                                cartLine.isPromotionItem ||
                                cart.type === "Job" ||
                                isLoading[cart.id] ||
                                isQuoteCart
                            }
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            data-test-selector="cartline_qty"
                            onKeyPress={keyPressHandler}
                        />
                        <Clickable
                            style={{
                                position: "absolute",
                                top: "2.1rem",
                                left: ".1rem",
                                height: "38px",
                                background: "rgba(104, 172, 247, 8%)",
                                padding: "10px",
                                fontSize: "1.4rem",
                                color: "#002554",
                                borderBottomLeftRadius: "2",
                                borderTopLeftRadius: "2",
                            }}
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            onClick={() => decreaseOrderedProductQty()}
                            disabled={isQuoteCart}
                        >
                            <Icon src="Minus" />
                        </Clickable>
                        <Clickable
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            onClick={() => increaseOrderedProductQty()}
                            disabled={isQuoteCart}
                            style={{
                                position: "absolute",
                                top: "2.1rem",
                                right: ".08rem",
                                height: "38px",
                                background: "rgba(104, 172, 247, 8%)",
                                padding: "10px",
                                fontSize: "1.4rem",
                                color: "#002554",
                                borderBottomRightRadius: "2",
                                borderTopRightRadius: "2",
                            }}
                        >
                            <Icon src="Plus" />
                        </Clickable>
                    </StyledWrapper>
                </GridItem>
                {(product.minimumOrderQty > 0 || multipleSaleQty > 1) && (
                    <GridItem width={12} {...styles.minMultiGridItem}>
                        {product.minimumOrderQty > 0 && (
                            <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                                <Typography {...styles.minQtyLabel}>
                                    {translate("Minimum order quantity: ")}
                                </Typography>
                                <Typography {...styles.minQtyValue}>
                                    {FormattedCommaSeparatedValue(product.minimumOrderQty)}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {multipleSaleQty > 1 && (
                            <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                                <Typography {...styles.minQtyLabel}>
                                    {translate("Order in multiples of: ")}
                                </Typography>
                                <Typography {...styles.minQtyValue}>
                                    {FormattedCommaSeparatedValue(multipleSaleQty)}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </GridItem>
                )}
            </GridContainer>
        );
    }

    return (
        <GridContainer gap={8} style={{ display: "block", width: "100%" }}>
            <GridItem width={12}>
                <StyledWrapper>
                    <SmallHeadingAndText
                        {...styles.readOnlyQuantityText}
                        heading={label || translate("Quantity:")}
                        text={FormattedCommaSeparatedValue(cartLine.qtyOrdered!)}
                    />
                </StyledWrapper>
            </GridItem>
            {(product.minimumOrderQty > 0 || multipleSaleQty > 1) && (
                <GridItem width={12} {...styles.minMultiGridItem}>
                    {product.minimumOrderQty > 0 && (
                        <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                            <Typography {...styles.minQtyLabel}>
                                {translate("Minimum: ")}
                            </Typography>
                            <Typography {...styles.minQtyValue}>
                                {FormattedCommaSeparatedValue(product.minimumOrderQty)}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {multipleSaleQty > 1 && (
                        <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                            <Typography {...styles.minQtyLabel}>
                                {translate("Multiples of: ")}
                            </Typography>
                            <Typography {...styles.minQtyValue}>
                                {FormattedCommaSeparatedValue(multipleSaleQty)}
                            </Typography>
                        </StyledWrapper>
                    )}
                </GridItem>
            )}            
        </GridContainer>
    );
};

const mapCartLineToProduct = (cartLine: CartLineModel, fetchedProduct?: ProductModel) => {
    const product = cloneDeep(cartLine) as any as ProductModel;
    product.id = cartLine.productId || "";
    if (fetchedProduct) {
        product.minimumOrderQty = fetchedProduct.minimumOrderQty;
        if (fetchedProduct.detail) {
            product.detail = fetchedProduct.detail;
        }
    }
    return product;
};

export default withCartLine(connect(mapStateToProps, mapDispatchToProps)(CartLineQuantity));